/**
 * @fileoverview Two-factor verification API calls.
 * @description verifyTwoFactorCode calls POST /identity/mfa/verify and resendTwoFactorCode calls
 * POST /identity/mfa/resend, both keyed by the challengeId issued by /identity/login — the backend
 * looks up the pending challenge from it server-side, so no email/expiry needs to round-trip from
 * the client. Both also send this device's id (see device-identity.utils.ts), attached internally
 * rather than by every caller. verifyTwoFactorCode's response carries no role/profile fields (nor
 * a refresh token — that's an httpOnly cookie now) or email, so useVerifyTwoFactor.ts follows up
 * with a GET /identity/me for role/profile and reads email from the pending challenge.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'
import { getOrCreateDeviceId } from '@/shared/lib/device/device-identity.utils'

export interface VerifyTwoFactorRequest {
  challengeId: string
  otp: string
}

interface VerifyTwoFactorRequestBody extends VerifyTwoFactorRequest {
  clientDeviceId: string
}

export interface SessionResponse {
  authenticationStatus: 'SUCCESS'
  accessToken: string
  tokenType: 'Bearer'
  accessTokenExpiresAt: string
  sessionId: string
  sessionIdleExpiresAt: string
  sessionAbsoluteExpiresAt: string
  /** When true the session is restricted: only GET /identity/me and Change Password are usable
   * (every other route answers 403 / appCode 1043) until the password is changed. */
  mustChangePassword: boolean
}

export interface ResendTwoFactorRequest {
  challengeId: string
}

interface ResendTwoFactorRequestBody extends ResendTwoFactorRequest {
  clientDeviceId: string
}

export interface ResendTwoFactorResponse {
  challengeId: string
  channel: string
  destinationMasked: string
  expiresAt: string
  resendAvailableAt: string
}

/**
 * @description Submits the entered verification code to complete sign-in, along with this
 * device's ID.
 * @param {VerifyTwoFactorRequest} payload - The pending challenge's id and the entered code.
 * @returns {Promise<SessionResponse>} The newly issued access token.
 */
export async function verifyTwoFactorCode(
  payload: VerifyTwoFactorRequest,
): Promise<SessionResponse> {
  const body: VerifyTwoFactorRequestBody = { ...payload, clientDeviceId: getOrCreateDeviceId() }
  const { data } = await httpClient.post<SessionResponse>('/identity/mfa/verify', body)
  return data
}

/**
 * @description Requests a fresh verification code for a challenge already awaiting one,
 * invalidating the previous code, along with this device's ID.
 * @param {ResendTwoFactorRequest} payload - The pending challenge's id.
 * @returns {Promise<ResendTwoFactorResponse>} The challenge's refreshed id/expiry/cooldown.
 */
export async function resendTwoFactorCode(
  payload: ResendTwoFactorRequest,
): Promise<ResendTwoFactorResponse> {
  const body: ResendTwoFactorRequestBody = { ...payload, clientDeviceId: getOrCreateDeviceId() }
  const { data } = await httpClient.post<ResendTwoFactorResponse>('/identity/mfa/resend', body)
  return data
}
